import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CLink, CText } from '@carrefour/design-system-components-react';
import { useTranslation } from 'react-i18next';
import { InfoBullType } from '../../../types/infoBull';
import { ListDetention, ListDetentionTypology } from '../../../types/operational';
import { InfoBull } from '../../InfoBull';
import { useStores } from '../../../context/storesContext';
import { formatPercentage } from '../../../utils/numberUtils';
import MrclText from '../../marcel/MrclText';

const CardWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`;

const DetailLink = styled(CLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #0970e6;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PerformanceHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Title = styled(MrclText)`
  text-align: left;
  align-self: flex-start;
  font-weight: 700;
  font-size: var(--font-size-body-m, 16px);
  line-height: var(--line-height-body-m, 24px);
  letter-spacing: var(--letter-spacing-normal, normal);
  color: #374151;
`;

const DetentionCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  flex: 1;

  > * {
    border: 1px solid #e5e7eb;
  }

  @media (max-width: 768px),
    only screen and (orientation: landscape) and (max-height: 700px) and (max-width: 1024px),
    only screen and (max-height: 560px) and (orientation: landscape),
    only screen and (max-width: 960px) and (orientation: landscape) {
    grid-template-columns: 1fr;
  }
`;

const CustDetentionCard = styled.div`

  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  overflow: hidden;
  transition: transform 0.15s ease;

  > * {
    height: 100%;
    margin-bottom: 0;
  }
`;

export interface LandingDetentionTopCardsProps {
  detentionTop200PgcData?: ListDetention | null;
  detention3000PgcData?: ListDetention | null;
  detentionTypologyData?: ListDetentionTypology | null;
  detentionPgcError: boolean;
  detentionTop200PgcError: boolean;
  detentionTypologyError: boolean;
}

export const LandingDetentionTopCards: React.FC<LandingDetentionTopCardsProps> = ({
  detentionTop200PgcData,
  detention3000PgcData,
  detentionTypologyData,
  detentionPgcError,
  detentionTop200PgcError,
  detentionTypologyError,
}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const { selectedStore } = useStores();

  const handleCardClick = () => {
    window.scrollTo(0, 0);
    navigate('/operational/detention');
  };

  const buildInfoBull = (title: string, data: any, error: boolean): InfoBullType => {
      return {
        lastUpdated: data?.importedDateTime,
        description: title,
        loading: !data,
        isUnavailable: !data || data?.rate === null,
        error: error
    };
  };

  return (
      <CardWrapper>
        <PerformanceHeaderRow>
          <Title>Détention</Title>
          <DetailLink onClick={handleCardClick}>
            Voir le détail
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </DetailLink>
        </PerformanceHeaderRow>
        <DetentionCardsContainer data-testid="detention-cards-container">
          <CustDetentionCard>
              <PercentageCard
                title={t('operationnel.top_3000_pgc')}
                data={detention3000PgcData}
                infoBull={buildInfoBull(t('operationnel.detention_top_3000_pgc'), detention3000PgcData as ListDetention, detentionPgcError)}
              />
          </CustDetentionCard>

          {selectedStore?.affiliation === "FRANCHISE" ?
          (<CustDetentionCard>
            <PercentageCard
            title={t('operationnel.detention_t1_rff')}
            data={detentionTypologyData}
            infoBull={buildInfoBull(t('operationnel.detention_t1'), detentionTypologyData, detentionTypologyError)}
            />
          </CustDetentionCard>) :
          (<CustDetentionCard>
            <PercentageCard
            title={t('operationnel.detention_top_200_pgc')}
            data={detentionTop200PgcData}
            infoBull={buildInfoBull(t('operationnel.detention_top_200_pgc'), detentionTop200PgcData as ListDetention, detentionTop200PgcError)}
            />
          </CustDetentionCard>)
          }
        </DetentionCardsContainer>
      </CardWrapper>
  );
};

const PercentageCardWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 6px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  user-select: none;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const InfoBullContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MetricWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 12px;
`;

interface PercentageCardProps {
  title: string;
  data?: ListDetention | ListDetentionTypology | null;
  infoBull?: InfoBullType;
}

const PercentageCard: React.FC<PercentageCardProps> = ({
  title,
  data,
  infoBull,
}) => {
  const daySelected = data && 'daySelected' in data ? data?.daySelected : '';

  const formattedRate =
    data?.rate != null && !Number.isNaN(data.rate)
      ? formatPercentage(data.rate as number)
      : null;
  const displayRate =
    formattedRate && !formattedRate.toLowerCase().includes('nan')
      ? formattedRate
      : '-';

  return (
    <PercentageCardWrapper>
      <HeaderSection>
        <CText
          tagStyle="h3"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: '#111827',
          }}
        >
          {title} {daySelected}
        </CText>
        <InfoBullContainer onClick={(e) => e.stopPropagation()}>
          <InfoBull
              description={infoBull?.description as string}
              lastUpdated={infoBull?.lastUpdated}
              loading={infoBull?.loading}
              isUnavailable={infoBull?.isUnavailable}
              error={infoBull?.error}
              onOpenTracking={() => {}}
          />
        </InfoBullContainer>
      </HeaderSection>

      <MetricWrapper>
        <CText
          style={{
            fontSize: '48px',
            fontWeight: 900,
            letterSpacing: '-0.05em',
            color: '#a10e13',
            lineHeight: 1,
          }}
        >
          {displayRate}
        </CText>
      </MetricWrapper>
    </PercentageCardWrapper>
  );
};

export default PercentageCard;